import { Injectable, inject } from "@angular/core";
import { HttpClient, HttpHeaders, HttpParams } from "@angular/common/http";
import { forkJoin, map, Observable } from "rxjs";
import { environment } from "../../../../environments/environment";
import {
  Customer,
  CustomerListResponse,
  CustomerSummary,
} from "../interfaces/customer.interface";
import { CustomerFilter } from "../interfaces/customer-filter.interface";
import { PaymentMethod } from "../interfaces/customer-payment-method.interface";
import { CustomerHistoryEntry } from "../interfaces/customer-history.interface";
import { PaymentIntegration } from "../interfaces/payment-integration.interface";
import { QPilotAccessToken } from "../../../core/services/qpilot.service";

@Injectable({ providedIn: "root" })
export class CustomersService {
  private http = inject(HttpClient);
  // Base API URL matching old QMC pattern: environment.qpilotApiUrl + "/api"
  private apiUrl = `${environment.qpilotApiUrl}/api`;

  /**
   * Get customer summaries for a site (list view)
   * Old QMC: GET /api/Sites/{siteId}/Customers/Summaries?page=1&pageSize=25&...
   * See qpilot.service.ts:getCustomerSummariesForSite()
   * @param filter - Customer filter parameters
   * @param accessToken - Optional access token for embedded mode
   */
  getCustomerSummaries(
    filter: CustomerFilter,
    accessToken?: QPilotAccessToken,
  ): Observable<CustomerSummary[]> {
    const params = this.buildFilterParams(filter);
    const headers = this.createHeaders(accessToken);
    return this.http.get<CustomerSummary[]>(
      `${this.apiUrl}/Sites/${filter.siteId}/Customers/Summaries`,
      { params, headers },
    );
  }

  /**
   * Get customer summaries count for a site
   * Old QMC: GET /api/Sites/{siteId}/Customers/Summaries/Count?...
   * See qpilot.service.ts:getCustomerSummariesForSiteCount()
   * @param filter - Customer filter parameters
   * @param accessToken - Optional access token for embedded mode
   */
  getCustomerSummariesCount(
    filter: CustomerFilter,
    accessToken?: QPilotAccessToken,
  ): Observable<number> {
    const params = this.buildFilterParams(filter);
    const headers = this.createHeaders(accessToken);
    return this.http.get<number>(
      `${this.apiUrl}/Sites/${filter.siteId}/Customers/Summaries/Count`,
      { params, headers },
    );
  }

  /**
   * Get customers with pagination (combines summaries + count)
   * This matches the old QMC pattern where:
   * - fetchCustomers() calls getCustomerSummariesForSite()
   * - fetchCount() calls getCustomerSummariesForSiteCount()
   * Both are called together and results combined for the list view.
   *
   * @param filter - Customer filter parameters
   * @param accessToken - Optional access token for embedded mode
   * @returns Observable with data array and pagination info
   */
  getCustomers(
    filter: CustomerFilter,
    accessToken?: QPilotAccessToken,
  ): Observable<CustomerListResponse> {
    return forkJoin({
      data: this.getCustomerSummaries(filter, accessToken),
      count: this.getCustomerSummariesCount(filter, accessToken),
    }).pipe(
      map(({ data, count }) => ({
        data,
        pagination: {
          page: filter.page,
          pageSize: filter.pageSize,
          totalItems: count,
          totalPages: Math.ceil(count / filter.pageSize),
        },
      })),
    );
  }

  /**
   * Soft delete customer (sets deletedUtc, can be restored)
   * Old QMC: DELETE /Customers/{customerId}?hardDelete=false
   */
  softDeleteCustomer(customerId: number): Observable<void> {
    return this.deleteCustomer(customerId, false);
  }

  /**
   * Hard delete customer (permanent removal, cannot be restored)
   * Old QMC: DELETE /Customers/{customerId}?hardDelete=true
   */
  hardDeleteCustomer(customerId: number): Observable<void> {
    return this.deleteCustomer(customerId, true);
  }

  /**
   * Get single customer by ID
   * Old QMC: GET /Customers/{customerId}
   * @param customerId - Customer ID
   * @param accessToken - Optional access token for embedded mode
   */
  getCustomer(
    customerId: number,
    accessToken?: QPilotAccessToken,
  ): Observable<Customer> {
    const headers = this.createHeaders(accessToken);
    return this.http.get<Customer>(`${this.apiUrl}/Customers/${customerId}`, {
      headers,
    });
  }

  /**
   * Update customer
   * Old QMC: PUT /Customers/{customerId}
   * @param customerId - Customer ID
   * @param data - Customer data to update
   * @param accessToken - Optional access token for embedded mode
   */
  updateCustomer(
    customerId: number,
    data: Partial<Customer>,
    accessToken?: QPilotAccessToken,
  ): Observable<Customer> {
    const headers = this.createHeaders(accessToken);
    return this.http.put<Customer>(
      `${this.apiUrl}/Customers/${customerId}`,
      data,
      { headers },
    );
  }

  /**
   * Delete customer
   * Old QMC: DELETE /Customers/{customerId}?hardDelete={bool}
   * See qpilot.service.ts:deleteCustomer()
   * @param hardDelete - false for soft delete (sets deletedUtc), true for permanent removal
   */
  deleteCustomer(customerId: number, hardDelete: boolean): Observable<void> {
    return this.http.delete<void>(
      `${this.apiUrl}/Customers/${customerId}?hardDelete=${hardDelete}`,
    );
  }

  /**
   * Restore soft-deleted customer (undelete)
   * Old QMC: PUT /Customers/{customerId}/undelete
   */
  restoreCustomer(customerId: number): Observable<Customer> {
    return this.http.put<Customer>(
      `${this.apiUrl}/Customers/${customerId}/undelete`,
      {},
    );
  }

  /**
   * Get customer payment methods
   * Old QMC: GET /Customers/{customerId}/PaymentMethods
   * @param customerId - Customer ID
   * @param accessToken - Optional access token for embedded mode
   */
  getPaymentMethods(
    customerId: number,
    accessToken?: QPilotAccessToken,
  ): Observable<PaymentMethod[]> {
    const headers = this.createHeaders(accessToken);
    return this.http.get<PaymentMethod[]>(
      `${this.apiUrl}/Customers/${customerId}/PaymentMethods`,
      { headers },
    );
  }

  /**
   * Delete payment method
   * Old QMC: DELETE /Customers/{customerId}/PaymentMethods/{paymentMethodId}
   */
  deletePaymentMethod(
    customerId: number,
    paymentMethodId: number,
  ): Observable<void> {
    return this.http.delete<void>(
      `${this.apiUrl}/Customers/${customerId}/PaymentMethods/${paymentMethodId}`,
    );
  }

  /**
   * Set default payment method
   * Old QMC: POST /Customers/{customerId}/PaymentMethods/{paymentMethodId}/SetDefault
   */
  setDefaultPaymentMethod(
    customerId: number,
    paymentMethodId: number,
  ): Observable<void> {
    return this.http.post<void>(
      `${this.apiUrl}/Customers/${customerId}/PaymentMethods/${paymentMethodId}/SetDefault`,
      {},
    );
  }

  /**
   * Get customer event logs (history)
   * Old QMC: GET /Customers/{customerId}/EventLogs
   * API returns PascalCase, we map to camelCase
   * @param customerId - Customer ID
   * @param accessToken - Optional access token for embedded mode
   */
  getCustomerHistory(
    customerId: number,
    accessToken?: QPilotAccessToken,
  ): Observable<CustomerHistoryEntry[]> {
    interface ApiEventLog {
      Id: number;
      EventUtc: string;
      UserId?: number;
      SiteId?: number;
      ScheduledOrderId?: number;
      CustomerId?: number;
      PaymentMethodId?: number;
      ProductId?: number;
      EventType: string;
      EntityType: string;
      ExceptionType: string;
      EventOriginatorType: number;
      EventOriginatorId?: number;
      Description: string;
      OriginatorFormatted: string;
      EventVerb: string;
      DescriptionFormatted: string;
    }

    const headers = this.createHeaders(accessToken);
    return this.http
      .get<
        ApiEventLog[]
      >(`${this.apiUrl}/Customers/${customerId}/EventLogs`, { headers })
      .pipe(
        map((logs) =>
          logs
            .map((log) => ({
              id: log.Id,
              eventUtc: new Date(log.EventUtc),
              userId: log.UserId,
              siteId: log.SiteId,
              scheduledOrderId: log.ScheduledOrderId,
              customerId: log.CustomerId,
              paymentMethodId: log.PaymentMethodId,
              productId: log.ProductId,
              eventType: log.EventType || "",
              entityType: log.EntityType || "",
              exceptionType: log.ExceptionType || "None",
              eventOriginatorType: log.EventOriginatorType,
              eventOriginatorId: log.EventOriginatorId,
              description: log.Description || "",
              originatorFormatted: log.OriginatorFormatted || "",
              eventVerb: log.EventVerb || "",
              descriptionFormatted: log.DescriptionFormatted || "",
            }))
            // Sort by date descending (newest first) - matching old QMC
            .sort((a, b) => b.eventUtc.valueOf() - a.eventUtc.valueOf()),
        ),
      );
  }

  /**
   * Get payment integrations for a site
   * Old QMC: qpilot.service.ts -> getPaymentIntegrationsForSite()
   * @param siteId - Site ID
   * @param accessToken - Optional access token for embedded mode
   */
  getPaymentIntegrationsForSite(
    siteId: number,
    accessToken?: QPilotAccessToken,
  ): Observable<PaymentIntegration[]> {
    const headers = this.createHeaders(accessToken);
    return this.http
      .get<{
        Items: PaymentIntegration[];
      }>(`${this.apiUrl}/Sites/${siteId}/PaymentIntegrations`, { headers })
      .pipe(map((response) => response.Items || []));
  }

  /**
   * Create a new payment method for a customer
   * Old QMC: qpilot.service.ts -> createPaymentMethod()
   * @param paymentMethod - Payment method data
   * @param accessToken - Optional access token for embedded mode
   */
  createPaymentMethod(
    paymentMethod: Partial<PaymentMethod>,
    accessToken?: QPilotAccessToken,
  ): Observable<PaymentMethod> {
    const headers = this.createHeaders(accessToken);
    return this.http.post<PaymentMethod>(
      `${this.apiUrl}/PaymentMethods`,
      paymentMethod,
      { headers },
    );
  }

  /**
   * Get payment methods from external gateway (Braintree)
   * Old QMC: qpilot.service.ts -> getOtherPaymentMethods()
   * @param siteId - Site ID
   * @param gatewayCustomerId - Gateway customer ID
   * @param paymentIntegrationType - Payment integration type
   * @param accessToken - Optional access token for embedded mode
   */
  getOtherPaymentMethods(
    siteId: number,
    gatewayCustomerId: string,
    paymentIntegrationType: string,
    accessToken?: QPilotAccessToken,
  ): Observable<PaymentMethod[]> {
    const headers = this.createHeaders(accessToken);
    return this.http.get<PaymentMethod[]>(
      `${this.apiUrl}/Sites/${siteId}/OtherPaymentMethods/${gatewayCustomerId}/${paymentIntegrationType}`,
      { headers },
    );
  }

  /**
   * Build HTTP params from filter object
   * Matches old QMC QPilotFilterHelper.createQueryParameters()
   * Old QMC filters: search, Start Date/End Date, Filter by Deleted
   *
   * Query params pattern: page=1&pageSize=20&orderBy=Id&order=asc&status=Active&offset=-5&active=true&valid=true
   *
   * Note: siteId is NOT included in params - it's in the URL path
   */
  private buildFilterParams(filter: CustomerFilter): HttpParams {
    let params = new HttpParams()
      .set("page", filter.page.toString())
      .set("pageSize", filter.pageSize.toString());

    // Sorting - Old QMC: orderBy=Id&order=asc
    if (filter.orderBy) {
      params = params.set("orderBy", filter.orderBy);
    }
    if (filter.order) {
      params = params.set("order", filter.order);
    }

    // Status filter - Old QMC: status=Active (default)
    if (filter.status) {
      params = params.set("status", filter.status);
    }

    // Timezone offset - Old QMC: offset=-5 (from site.UtcOffset)
    if (filter.offset !== undefined && filter.offset !== null) {
      params = params.set("offset", filter.offset.toString());
    }

    // Active filter - Old QMC: active=${!filter.includeInactive}
    // Default: includeInactive=false → active=true
    if (
      filter.includeInactive !== undefined &&
      filter.includeInactive !== null
    ) {
      params = params.set("active", (!filter.includeInactive).toString());
    }

    // Valid filter - Old QMC: valid=${!filter.includeInvalid}
    // Default: includeInvalid=false → valid=true
    if (filter.includeInvalid !== undefined && filter.includeInvalid !== null) {
      params = params.set("valid", (!filter.includeInvalid).toString());
    }

    // Search - Old QMC: search input
    if (filter.search) {
      params = params.set("search", filter.search);
    }

    // Date filters - Old QMC: Start Date / End Date
    if (filter.customerCreatedFromDate) {
      params = params.set(
        "customerCreatedFromDate",
        filter.customerCreatedFromDate.toISOString(),
      );
    }
    if (filter.customerCreatedToDate) {
      params = params.set(
        "customerCreatedToDate",
        filter.customerCreatedToDate.toISOString(),
      );
    }

    // Deleted filter - Old QMC: Filter by Deleted checkbox
    if (filter.isDeleted) {
      params = params.set("isDeleted", "true");
    }

    return params;
  }

  /**
   * Creates HTTP headers for authenticated requests
   * Follows the same pattern as QPilotService.createHeaders()
   * @param accessToken - Optional access token for embedded mode
   * @returns HTTP headers with Authorization if token provided
   */
  private createHeaders(accessToken?: QPilotAccessToken): HttpHeaders {
    let headers = new HttpHeaders();

    if (accessToken) {
      if (accessToken.TokenBearerAuth) {
        headers = headers.set(
          "Authorization",
          `Bearer ${accessToken.TokenBearerAuth}`,
        );
      } else if (accessToken.TokenAuth) {
        headers = headers.set(
          "Authorization",
          `Basic ${accessToken.TokenAuth}`,
        );
      }
    }

    return headers;
  }
}
